import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpPublic } from "@/lib/api";
import {
  Flower, Feather, ArrowRight, TreeDeciduous, Users,
  BookOpen, Newspaper, Search, MapPin, Heart, Sparkles,
  Camera, Video, ScrollText, Bell, Globe2, ShieldCheck,
} from "lucide-react";
import ObituariesTabs from "@/components/ObituariesTabs";
import MemorialDesignShowcase from "@/components/memorials/MemorialDesignShowcase";
import SeoHead from "@/components/SeoHead";
import MemorialBadges from "@/components/MemorialBadges";

// -----------------------------------------------------------------------------
// /obituaries/hub — the rich "Memorials & Obituaries" landing page.
//
// Renders the site-agnostic article on remembrance + a scannable overview
// of everything the platform offers (submission, family-tree links,
// discovery, community). Above and below the article we pull in the
// most-recent photo-bearing obituaries + featured memorials so the page
// feels alive, not static.
//
// The page is deliberately branded as "My Family Tree Memorials &
// Obituaries" rather than "Social Pulse" so it renders unchanged on the
// sibling sites (myfamilytree.care, indianfamilytree.com) — all three
// pull from the same X-Site-Source backend.
// -----------------------------------------------------------------------------

export default function ObituariesHub() {
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    setLoadingRecent(true);
    httpPublic.get("/obituaries", { params: { limit: 30 } })
      .then((r) => {
        const withPhotos = (r.data.items || []).filter((m) => m.portrait).slice(0, 8);
        setRecent(withPhotos);
      })
      .catch(() => setRecent([]))
      .finally(() => setLoadingRecent(false));
  }, []);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid="obituaries-hub-page">
      <SeoHead section="obituaries" url="https://socialpulse.com/obituaries/hub" />
      <ObituariesTabs position="top" />

      {/* Hero ---------------------------------------------------------------- */}
      <section className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start pt-4">
        <div>
          <div className="overline mb-3" style={{ color: "#7C3AED" }}>
            MEMORIALS &amp; OBITUARIES · MY FAMILY TREE
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            Every life has a story{" "}
            <span style={{ color: "#7C3AED" }}>worth remembering.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            A permanent, dignified home for the people who shaped us — one story,
            one family, one memory at a time. Publish an obituary in minutes,
            connect your loved one to a living family tree, and receive
            heartfelt tributes from anywhere in the world.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="hub-hero-ctas">
            <Link
              to="/obituaries/submit"
              data-testid="hub-cta-create"
              className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-5 !py-3"
            >
              <Feather className="w-4 h-4" /> Create a Memorial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/obituaries"
              data-testid="hub-cta-recent"
              className="wofy-btn-ghost inline-flex items-center gap-2 !text-base !px-5 !py-3"
            >
              <Newspaper className="w-4 h-4" /> Recent Obituaries
            </Link>
            <Link
              to="/celebrate"
              data-testid="hub-cta-search"
              className="wofy-btn-ghost inline-flex items-center gap-2 !text-base !px-5 !py-3"
            >
              <Search className="w-4 h-4" /> Search Memorials
            </Link>
          </div>

          <div className="mt-8 flex items-center flex-wrap gap-x-6 gap-y-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            <span className="inline-flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> Global archive</span>
            <span className="inline-flex items-center gap-1.5"><TreeDeciduous className="w-3.5 h-3.5" /> Linked to family trees</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Privacy controls</span>
            <span className="inline-flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Anniversary reminders</span>
          </div>
        </div>

        <RecentPreview items={recent} loading={loadingRecent} />
      </section>

      <MemorialDesignShowcase />

      {/* Article ------------------------------------------------------------- */}
      <article
        className="mt-20 max-w-4xl mx-auto prose prose-neutral prose-lg prose-headings:font-display prose-headings:tracking-tight"
        data-testid="hub-article"
      >
        <h2>Welcome to the My Family Tree Memorials &amp; Obituaries</h2>
        <p>
          Every life leaves an imprint on the hearts of family, friends, and the
          community. While our loved ones may no longer be with us, their
          stories, values, achievements, and the memories they created continue
          to shape future generations.
        </p>
        <p>
          The <strong>My Family Tree Memorials &amp; Obituaries</strong> section
          was created with a simple yet meaningful vision:{" "}
          <em>
            to ensure that every person is remembered with dignity, respect,
            and lasting honor, while preserving their story for future
            generations.
          </em>
        </p>
        <p>
          Too often, a person's memories are scattered across old photographs,
          fading newspaper notices, social media posts, or family conversations
          that may one day be forgotten. We believe every individual —
          regardless of their background, wealth, nationality, religion, or
          achievements — deserves a permanent place where their life can be
          celebrated and remembered.
        </p>
        <blockquote>
          A memorial is much more than a death announcement. It is a
          celebration of a life lived.
        </blockquote>
        <p>
          It is a place where families can preserve treasured photographs,
          record family history, share stories and milestones, document
          achievements, and collect heartfelt tributes from relatives and
          friends around the world. Future generations will be able to discover
          not only names and dates, but the personalities, experiences,
          traditions, and relationships that made each person unique.
        </p>
        <p>
          Through integration with{" "}
          <Link to="/family-tree-guide" data-testid="hub-article-family-link">
            My Family Tree
          </Link>
          , every memorial becomes part of your family's living history.
          Parents, grandparents, siblings, spouses, children, and future
          descendants can remain connected across generations, creating a
          lasting legacy that strengthens family identity and preserves
          invaluable genealogical information.
        </p>
        <p>
          Whether your loved one passed away recently or many decades ago, it
          is never too late to honor their memory. Every story added today
          becomes a priceless gift for tomorrow's generations.
        </p>

        <h3>We Invite You to Create a Memorial</h3>
        <p>
          If someone you love has touched your life, we warmly invite you to
          create a memorial page in their honor. You can:
        </p>
        <ul>
          <li>Share their life story and biography.</li>
          <li>Upload photographs and videos.</li>
          <li>Record important milestones and achievements.</li>
          <li>Add funeral or memorial service information.</li>
          <li>Connect them to your family tree.</li>
          <li>Receive messages of condolence from family and friends.</li>
          <li>Preserve memories that future generations can cherish.</li>
          <li>
            Celebrate birthdays and anniversaries with remembrance
            notifications.
          </li>
          <li>Build a lasting digital legacy that can be shared worldwide.</li>
        </ul>
        <p>
          Every contribution, whether large or small, helps preserve a family's
          history and ensures that cherished memories are never lost.
        </p>

        <h3>A Community of Remembrance</h3>
        <p>
          Our mission extends beyond preserving individual memories. We aspire
          to build one of the world's most comprehensive and respectful
          memorial archives — where families, historians, genealogists, and
          communities can come together to honor lives, strengthen family
          connections, and protect our shared human heritage.
        </p>
        <p>
          Together, we can create a living record of humanity — one life, one
          family, and one story at a time.
        </p>

        <h3>Begin Their Legacy Today</h3>
        <p>
          Honor someone who made a difference in your life. Create a memorial.
          Preserve their story. Connect them to your family tree. Share their
          legacy with future generations.
        </p>
        <p>
          <em>
            Because every life matters, every story deserves to be remembered,
            and every memory is a treasure worth preserving.
          </em>
        </p>
      </article>

      {/* Prominent CTA row inside/below the article ------------------------- */}
      <section
        data-testid="hub-cta-row"
        className="mt-12 max-w-4xl mx-auto rounded-3xl p-8 md:p-10 grid md:grid-cols-[1.4fr_1fr] gap-8 items-center"
        style={{
          background: "linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(236, 72, 153, 0.06))",
          border: "1px solid rgba(139, 92, 246, 0.25)",
        }}
      >
        <div>
          <div className="overline mb-2" style={{ color: "#7C3AED" }}>Begin their legacy</div>
          <h3 className="font-display text-3xl md:text-4xl tracking-tight leading-tight">
            Honor someone who <br className="hidden md:block" />
            made a difference.
          </h3>
          <p className="mt-3 text-sm md:text-base" style={{ color: "var(--wofy-text-soft)" }}>
            Create a memorial in a few minutes. Add photos, stories, funeral
            details, and invite family from anywhere in the world to leave
            memories, candles and condolences.
          </p>
        </div>
        <div className="flex flex-col gap-3">
          <Link
            to="/obituaries/submit"
            data-testid="hub-cta-row-create"
            className="wofy-btn-primary inline-flex items-center justify-center gap-2 !text-base !px-5 !py-3"
          >
            <Feather className="w-4 h-4" /> Create a Memorial <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/obituaries"
            data-testid="hub-cta-row-recent"
            className="wofy-btn-ghost inline-flex items-center justify-center gap-2 !text-base"
          >
            <Newspaper className="w-4 h-4" /> Browse Recent Obituaries
          </Link>
          <Link
            to="/family-tree-guide"
            data-testid="hub-cta-row-guide"
            className="wofy-btn-ghost inline-flex items-center justify-center gap-2 !text-base"
          >
            <TreeDeciduous className="w-4 h-4" /> Start a Family Tree
          </Link>
        </div>
      </section>

      {/* What you can do — visual grid -------------------------------------- */}
      <section className="mt-20" data-testid="hub-features">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="overline mb-2" style={{ color: "#7C3AED" }}>Everything a memorial can be</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tight">
            One page — a lifetime of memory.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { icon: ScrollText, title: "Life story", body: "A biography, achievements, milestones, and the personality that made them them." },
            { icon: Camera,     title: "Photo gallery", body: "Upload photographs from every chapter — childhood, weddings, travels, family reunions." },
            { icon: Video,      title: "Videos & audio", body: "Home videos, voice recordings, tribute reels — everything future generations should hear." },
            { icon: TreeDeciduous, title: "Family tree link", body: "Automatically connect parents, spouses, children and cousins across generations." },
            { icon: Heart,      title: "Condolences & candles", body: "Family and friends worldwide can leave messages, light candles, and share stories." },
            { icon: Bell,       title: "Anniversary reminders", body: "Followers receive gentle reminders on birthdays, death anniversaries and important dates." },
            { icon: MapPin,     title: "Funeral & cemetery", body: "Service times, livestream links, directions, dress code, and cemetery locations." },
            { icon: Sparkles,   title: "AI memorial cards", body: "A tasteful shareable memorial card is generated automatically from the photo you upload." },
            { icon: ShieldCheck,title: "Privacy controls", body: "Public, family-only, invitation-only or password-protected — you choose the visibility." },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                className="wofy-card"
                data-testid={`hub-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                     style={{ background: "rgba(139, 92, 246, 0.12)", color: "#7C3AED" }}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-display text-lg">{f.title}</div>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
                  {f.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Recent obituaries preview at bottom ------------------------------- */}
      {recent.length > 0 && (
        <section className="mt-20" data-testid="hub-recent-strip">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="overline mb-1" style={{ color: "#7C3AED" }}>Recently remembered</div>
              <h2 className="font-display text-2xl md:text-3xl tracking-tight">Today's obituaries</h2>
            </div>
            <Link to="/obituaries" data-testid="hub-recent-see-all" className="text-sm font-medium inline-flex items-center gap-1 hover:underline" style={{ color: "#7C3AED" }}>
              See all <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {recent.slice(0, 8).map((m) => (
              <Link
                key={m.id}
                to={`/celebrate/${m.slug || m.id}`}
                data-testid={`hub-recent-${m.id}`}
                className="wofy-card block hover:shadow-md transition-shadow"
              >
                <img
                  src={m.portrait}
                  alt={m.full_name || "Portrait"}
                  loading="lazy"
                  className="w-full aspect-square object-cover rounded-xl mb-3"
                />
                <div className="font-display text-base leading-tight truncate">
                  {m.name_latin || m.preferred_name || m.full_name}
                </div>
                {(m.verification_badges?.length > 0) && (
                  <MemorialBadges
                    badges={m.verification_badges}
                    size="sm"
                    className="mt-1"
                    testidPrefix={`hub-recent-badge-${m.id}`}
                  />
                )}
                <div className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
                  {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
                  {m.age_at_death && m.location?.city ? <span>·</span> : null}
                  {m.location?.city ? <span className="inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" />{m.location.city}</span> : null}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ObituariesTabs position="bottom" />
    </div>
  );
}

function RecentPreview({ items, loading }) {
  if (loading) {
    return (
      <div className="wofy-card min-h-[320px] flex items-center justify-center" data-testid="hub-recent-loading">
        <div className="text-xs opacity-60">Loading recent obituaries…</div>
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="wofy-card min-h-[320px] flex flex-col items-center justify-center text-center p-8" data-testid="hub-recent-empty">
        <Flower className="w-8 h-8 opacity-30" />
        <div className="font-display text-lg mt-3">No obituaries yet</div>
        <p className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
          Be the first to remember a loved one. Every story starts a legacy.
        </p>
      </div>
    );
  }
  const first = items[0];
  const rest = items.slice(1, 4);
  return (
    <aside className="wofy-card overflow-hidden" data-testid="hub-recent-hero">
      <div className="overline mb-3" style={{ color: "#7C3AED" }}>REMEMBERED TODAY</div>
      <Link to={`/celebrate/${first.slug || first.id}`} className="block group">
        <div className="relative aspect-[4/3] rounded-xl overflow-hidden">
          <img
            src={first.portrait}
            alt={first.full_name || "Portrait"}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="font-display text-2xl leading-tight drop-shadow">
              {first.name_latin || first.preferred_name || first.full_name}
            </div>
            <div className="text-[11px] opacity-90 mt-0.5">
              {first.age_at_death ? `Age ${first.age_at_death} · ` : ""}
              {first.location?.city || first.location?.state || first.location?.country || "—"}
            </div>
          </div>
        </div>
      </Link>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {rest.map((m) => (
          <Link
            key={m.id}
            to={`/celebrate/${m.slug || m.id}`}
            className="block rounded-lg overflow-hidden"
            title={m.full_name}
          >
            <img
              src={m.portrait}
              alt={m.full_name || "Portrait"}
              loading="lazy"
              className="w-full aspect-square object-cover"
            />
          </Link>
        ))}
      </div>
      <Link
        to="/obituaries"
        data-testid="hub-recent-view-all"
        className="mt-4 inline-flex items-center gap-1 text-sm font-medium hover:underline"
        style={{ color: "#7C3AED" }}
      >
        View all obituaries <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </aside>
  );
}
